import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { RAW_ITEM_UNITS, RAW_UNIT_SUFFIX, type RawItemDto, type RawItemUnit } from '@gc/shared';

import { api, ApiError } from '@/lib/api';

interface RawItemFormDialogProps {
  /** Null when adding. */
  rawItem: RawItemDto | null;
  onClose: () => void;
  onSaved: () => void;
}

/** Millidinars string -> whole IQD for the number input. Never a float. */
function toIqdInput(md: string | null): string {
  return md == null ? '' : (BigInt(md) / 1000n).toString();
}

/**
 * Add / edit a raw material (Wave 3, #17).
 *
 * Quantities are integers in the item's smallest unit (g / ml / pcs), the same
 * house rule as money. The cost figure is per KILO or LITRE for G/ML items and
 * per piece for PCS — the label under the field says which, because a per-gram
 * price typed here would be a thousandfold mistake.
 */
export function RawItemFormDialog({ rawItem, onClose, onSaved }: RawItemFormDialogProps) {
  const { t } = useTranslation();
  const editing = rawItem !== null;

  const [name, setName] = useState(rawItem?.name ?? '');
  const [unit, setUnit] = useState<RawItemUnit>(rawItem?.unit ?? 'G');
  const [openingQuantity, setOpeningQuantity] = useState('0');
  const [costIqd, setCostIqd] = useState(toIqdInput(rawItem?.costMd ?? null));
  const [lowThreshold, setLowThreshold] = useState(rawItem?.lowThreshold?.toString() ?? '');
  const [isActive, setIsActive] = useState(rawItem?.isActive ?? true);

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const inputClass =
    'w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary';

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="w-full max-w-md rounded-card border border-subtle bg-raised p-5 shadow-2xl"
      >
        <h2 className="mb-4 text-lg font-semibold text-content">
          {editing ? t('store.rawItems.editItem') : t('store.rawItems.addItem')}
        </h2>

        <label className="block">
          <span className="mb-1 block text-sm text-content-secondary">{t('common.name')}</span>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            autoFocus
            className={inputClass}
          />
        </label>

        <label className="mt-3 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('store.rawItems.unit')}
          </span>
          <select
            value={unit}
            onChange={(event) => setUnit(event.target.value as RawItemUnit)}
            className={inputClass}
          >
            {RAW_ITEM_UNITS.map((value) => (
              <option key={value} value={value}>
                {t(`store.rawItems.units.${value}`)}
              </option>
            ))}
          </select>
        </label>

        {editing ? (
          <p className="mt-3 rounded-control border border-subtle bg-input px-3 py-2 text-xs text-content-muted">
            {t('store.rawItems.stockIsLedgered', {
              stock: rawItem.stockQuantity,
              unit: RAW_UNIT_SUFFIX[rawItem.unit],
            })}
          </p>
        ) : (
          <label className="mt-3 block">
            <span className="mb-1 block text-sm text-content-secondary">
              {t('store.rawItems.openingQuantity', { unit: RAW_UNIT_SUFFIX[unit] })}
            </span>
            <input
              type="number"
              min="0"
              step="1"
              value={openingQuantity}
              onChange={(event) => setOpeningQuantity(event.target.value)}
              className={`${inputClass} tabular`}
            />
          </label>
        )}

        <label className="mt-3 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t(`store.rawItems.costPer.${unit}`)}
          </span>
          <input
            type="number"
            min="0"
            step="1"
            value={costIqd}
            onChange={(event) => setCostIqd(event.target.value)}
            placeholder={t('store.rawItems.costUnset')}
            className={`${inputClass} tabular`}
          />
        </label>

        <label className="mt-3 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('store.rawItems.lowThreshold', { unit: RAW_UNIT_SUFFIX[unit] })}
          </span>
          <input
            type="number"
            min="0"
            step="1"
            value={lowThreshold}
            onChange={(event) => setLowThreshold(event.target.value)}
            placeholder={t('store.rawItems.noThreshold')}
            className={`${inputClass} tabular`}
          />
        </label>

        {editing ? (
          <label className="mt-3 flex items-center gap-2 rounded-control border border-subtle bg-input px-3 py-2">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(event) => setIsActive(event.target.checked)}
              className="h-4 w-4 accent-[var(--brand-primary)]"
            />
            <span className="text-sm text-content">{t('store.rawItems.active')}</span>
          </label>
        ) : null}

        {error ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:opacity-60"
          >
            {t('common.save')}
          </button>
        </div>
      </form>
    </div>
  );

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      if (editing) {
        await api.patch(`/raw-items/${rawItem.id}`, {
          name: name.trim(),
          unit,
          costIqd: costIqd === '' ? null : Number(costIqd),
          lowThreshold: lowThreshold === '' ? null : Number(lowThreshold),
          isActive,
        });
      } else {
        await api.post('/raw-items', {
          name: name.trim(),
          unit,
          openingQuantity: Number(openingQuantity) || 0,
          // Omitted (not null) when unset — the create schema's optional.
          ...(costIqd === '' ? {} : { costIqd: Number(costIqd) }),
          ...(lowThreshold === '' ? {} : { lowThreshold: Number(lowThreshold) }),
        });
      }
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('store.rawItems.couldNotSave'));
    } finally {
      setSubmitting(false);
    }
  }
}
